import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type Result } from '@vello/core';
import { type ApiError, type FoodReviewAuditEntryView, type FoodReviewSubmissionView } from '@vello/api-client';
import { type FormatContext } from '@vello/format';
import { reviewAvailableActions, reviewQueueSummary, reviewTone, type ReviewAction, type ConsoleTone, formatMarketInstant, marketZoneOfContext, scopeTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Drawer, DrawerBody, DrawerHeader, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, StatCard, TextArea, TextInput, SectionTitle, Grid, Stack, Row as LayoutRow, Text, Strong, Ink, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import { PersonName } from '../components/person-name';
import { LoadingBlock } from '../components/loading-block';
import { IdRef } from '../components/code-ref';
import { DetailRow as Row } from '../components/detail-row';

const TONE_BADGE: Readonly<Record<ConsoleTone, 'neutral' | 'success' | 'danger' | 'warning'>> = { neutral: 'neutral', success: 'success', danger: 'danger', warning: 'warning' };
const CONFIRM_VARIANT: Readonly<Record<ReviewAction, 'primary' | 'secondary' | 'danger'>> = { claim: 'primary', approve: 'primary', request_changes: 'secondary', reject: 'danger', resubmit: 'primary', appeal: 'primary' };
/** D397: the four-eyes refusals the server names by reason code, each with its own explanation (`messages-session-actors.ts`). */

interface DetailState {
  readonly submission: FoodReviewSubmissionView;
  readonly version: number;
  readonly audit: readonly FoodReviewAuditEntryView[];
}

/**
 * **Revisão de cadastro Food** (onboarding review, M19/S194) — the reviewer surface over
 * `/v1/food-onboarding-review/*`. It shows the open queue, a submission's live status + its full append-only audit
 * trail (the "pendências"), and drives the decision lifecycle — claim, approve (four-eyes on high-risk kinds),
 * request changes, reject, resubmit, resolve an appeal. The acting reviewer is the signed-in operator; the server
 * enforces AAL2 + a scoped grant and the four-eyes / distinct-appeal-resolver rules.
 *
 * D397: no call names the reviewer — the server takes it from the session and refuses a body that names one, so the
 * second approval of a high-risk kind needs a different signed-in operator. A record written before that is labelled.
 */
export function FoodOnboardingReviewScreen(): ReactElement {
  const { runtime, me, scope: operatorScope, markets } = useAdmin();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  // D401: instants render in the operating market's zone (AGENTS: store UTC, render in the market zone).
  // When the scope names no single zone the render falls back to UTC and SAYS so (`formatMarketInstant`).
  const fmtCtx: FormatContext = { locale: lang, timeZone: scopeTimeZone(operatorScope.current, markets) ?? 'UTC', measurement: 'metric' };

  const [queue, setQueue] = useState<readonly FoodReviewSubmissionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorId, setErrorId] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [detail, setDetail] = useState<DetailState | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);

  /** `quiet` is the background re-read: the queue stays on screen (no loading block) while it runs. */
  const loadQueue = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) setLoading(true);
    setErrorId(undefined);
    const res = await runtime.foodOnboardingReview.listOpen();
    if (res.ok) setQueue(res.value.submissions);
    else setErrorId(t('food.err', { code: fail(res.error) }));
    if (!quiet) setLoading(false);
  }, [runtime, t]);

  const loadDetail = useCallback(
    async (submissionId: string): Promise<void> => {
      setSelectedId(submissionId);
      setDetailLoading(true);
      const res = await runtime.foodOnboardingReview.get(submissionId);
      setDetail(res.ok ? res.value : undefined);
      setDetailLoading(false);
    },
    [runtime],
  );

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);
  useAutoRefresh(() => loadQueue(true), { intervalMs: AUTO_REFRESH_MS.queue });

  const summary = useMemo(() => reviewQueueSummary(queue), [queue]);
  const afterDecision = useCallback((): void => {
    void loadQueue();
    if (selectedId !== undefined) void loadDetail(selectedId);
  }, [loadQueue, loadDetail, selectedId]);

  return (
    <>
      <PageHeader title={t('foodReview.title')} subtitle={t('foodReview.subtitle')} />

      <Grid min="xs" gap={4}>
        <StatCard label={t('foodReview.stat.total')} value={String(summary.total)} />
        <StatCard label={t('foodReview.stat.needsAction')} value={String(summary.needsAction)} />
        <StatCard label={t('foodReview.stat.pending')} value={String(summary.pending)} />
        <StatCard label={t('foodReview.stat.changesRequested')} value={String(summary.changesRequested)} />
        <StatCard label={t('foodReview.stat.appealed')} value={String(summary.appealed)} />
      </Grid>

      {errorId !== undefined && <div><Notice tone="danger">{errorId}</Notice></div>}

      <Panel padded={false}>
        <ListTable
          columns={[
            { key: 'ref', header: t('foodReview.col.ref'), render: (s: FoodReviewSubmissionView) => <Ink strong mono>{s.submissionRef}</Ink> },
            { key: 'kind', header: t('foodReview.col.kind'), render: (s: FoodReviewSubmissionView) => v('food.review_submission_kind', s.kind) },
            { key: 'subject', header: t('foodReview.col.subject'), render: (s: FoodReviewSubmissionView) => <IdRef value={s.subjectRef} /> },
            { key: 'status', header: t('foodReview.col.status'), render: (s: FoodReviewSubmissionView) => <Badge tone={TONE_BADGE[reviewTone(s.status)]}>{v('food.review_submission_status', s.status)}</Badge> },
          ]}
          rows={queue}
          rowKey={(s) => s.submissionId}
          onRowClick={(s) => void loadDetail(s.submissionId)}
          {...(selectedId !== undefined ? { selectedKey: selectedId } : {})}
          loading={loading}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('foodReview.queue.empty')} />}
        />
      </Panel>

      <Drawer open={selectedId !== undefined} onClose={() => { setSelectedId(undefined); setDetail(undefined); }} labelledBy="fr-detail-title">
        {selectedId !== undefined && (
          <>
            <DrawerHeader
              titleId="fr-detail-title"
              title={detail?.submission.submissionRef ?? queue.find((q) => q.submissionId === selectedId)?.submissionRef ?? ''}
              {...(detail !== undefined ? { subtitle: <Badge tone={TONE_BADGE[reviewTone(detail.submission.status)]}>{v('food.review_submission_status', detail.submission.status)}</Badge> } : {})}
              onClose={() => { setSelectedId(undefined); setDetail(undefined); }}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              {detailLoading ? (
                <LoadingBlock />
              ) : detail === undefined ? (
                <EmptyState title={t('foodReview.detail.none')} detail={t('foodReview.detail.none.detail')} />
              ) : (
                <SubmissionDetail detail={detail} reviewerId={me?.id} fmtCtx={fmtCtx} onDecided={afterDecision} />
              )}
            </DrawerBody>
          </>
        )}
      </Drawer>
    </>
  );
}

function SubmissionDetail({ detail, reviewerId, fmtCtx, onDecided }: { detail: DetailState; reviewerId: string | undefined; fmtCtx: FormatContext; onDecided: () => void }): ReactElement {
  const { runtime } = useAdmin();
  const { toast } = useConsole();
  const { t } = useT();
  const { fail, v, explain } = useVocab();
  const { submission } = detail;
  const actions = reviewAvailableActions(submission.status);
  const [pending, setPending] = useState<ReviewAction | undefined>(undefined);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<'approved' | 'rejected'>('approved');
  const [busy, setBusy] = useState(false);

  const reset = (): void => { setPending(undefined); setReasonCode(''); setNote(''); setOutcome('approved'); };

  const run = async (fn: () => Promise<Result<unknown, ApiError>>, okId: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const res = await fn();
    setBusy(false);
    if (res.ok) { reset(); toast({ message: t(okId), tone: 'success' }); onDecided(); }
    else toast({ message: explain(res.error) ?? t('food.err', { code: fail(res.error) }), tone: 'warning' });
  };

  const needsReason = (a: ReviewAction): boolean => a === 'approve' || a === 'reject' || a === 'request_changes' || a === 'appeal';
  const start = (a: ReviewAction): void => {
    if (reviewerId === undefined) return;
    if (a === 'claim') void run(() => runtime.foodOnboardingReview.claim(submission.submissionId), 'foodReview.toast.claimed');
    else if (a === 'resubmit') void run(() => runtime.foodOnboardingReview.resubmit(submission.submissionId), 'foodReview.toast.resubmitted');
    else setPending(a);
  };

  const confirm = (): void => {
    if (reviewerId === undefined || pending === undefined) return;
    const rc = reasonCode.trim();
    if (needsReason(pending) && rc.length === 0) return;
    const n = note.trim().length > 0 ? note.trim() : undefined;
    const id = submission.submissionId;
    if (pending === 'approve') void run(() => runtime.foodOnboardingReview.approve(id, rc, n), 'foodReview.toast.approved');
    else if (pending === 'reject') void run(() => runtime.foodOnboardingReview.reject(id, rc, n), 'foodReview.toast.rejected');
    else if (pending === 'request_changes') void run(() => runtime.foodOnboardingReview.requestChanges(id, rc, n), 'foodReview.toast.changesRequested');
    else if (pending === 'appeal') void run(() => runtime.foodOnboardingReview.appeal(id, { outcome, reasonCode: rc, ...(n !== undefined ? { note: n } : {}) }), 'foodReview.toast.appealResolved');
  };

  return (
    <Stack gap={4}>
      <section><Stack gap={3}>
        {submission.actorsFrom === 'LEGACY_BODY' && <div><Notice tone="warning">{t('actors.legacy.notice')}</Notice></div>}
        <Row label={t('foodReview.field.kind')}>{v('food.review_submission_kind', submission.kind)}</Row>
        <Row label={t('foodReview.field.subject')}><IdRef value={submission.subjectRef} copy full /></Row>
        <Row label={t('foodReview.field.submittedBy')} mono={false}><PersonName id={submission.submittedBy} /></Row>
        <Row label={t('foodReview.field.submittedAt')}>{formatMarketInstant(submission.submittedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)}</Row>
        <Row label={t('foodReview.field.version')}>{String(detail.version)}</Row>
        <Row label={t('foodReview.field.resubmissions')}>{String(submission.resubmissionCount)}</Row>
        {submission.firstApprovedBy !== undefined && <Row label={t('foodReview.field.firstApprovedBy')} mono={false}><PersonName id={submission.firstApprovedBy} /></Row>}
        {submission.rejectedBy !== undefined && <Row label={t('foodReview.field.rejectedBy')} mono={false}><PersonName id={submission.rejectedBy} /></Row>}
        {submission.lastReasonCode !== undefined && <Row label={t('foodReview.field.lastReason')}>{submission.lastReasonCode}</Row>}
      </Stack></section>

      <section>
        <SectionTitle>{t('foodReview.trail.title')}</SectionTitle>
        {detail.audit.length === 0 ? (
          <Text as="p" role="caption" tone="tertiary">{t('foodReview.trail.empty')}</Text>
        ) : (
          <Stack as="ol" gap={3}>
            {detail.audit.map((e, i) => (
              <li key={`${e.at}-${i}`} className="vello-admin-a-ruled-item">
                <LayoutRow gap={2} align="baseline" justify="between">
                  <Strong role="body">{v('food.review_audit_action', e.action)}</Strong>
                  <Text role="caption" tone="tertiary" mono>{formatMarketInstant(e.at, marketZoneOfContext(fmtCtx), fmtCtx.locale)}</Text>
                </LayoutRow>
                <Text as="div" role="caption" tone="secondary">{v('food.review_submission_status', e.fromStatus)} → {v('food.review_submission_status', e.toStatus)} · <PersonName id={e.actorRef} /></Text>
                {(e.reasonCode !== undefined || e.note !== undefined) && (
                  <Text as="div" role="caption" tone="tertiary">
                    {e.reasonCode !== undefined && <span>{e.reasonCode}</span>}
                    {e.reasonCode !== undefined && e.note !== undefined && <span aria-hidden="true"> · </span>}
                    {e.note !== undefined && <span>{e.note}</span>}
                  </Text>
                )}
              </li>
            ))}
          </Stack>
        )}
      </section>

      <section>
        <SectionTitle>{t('foodReview.decide.title')}</SectionTitle>
        {reviewerId === undefined ? (
          <Notice tone="warning">{t('foodReview.decide.noReviewer')}</Notice>
        ) : actions.length === 0 ? (
          <Text as="p" role="caption" tone="tertiary">{t('foodReview.decide.terminal')}</Text>
        ) : (
          <>
            <Text as="p" role="caption" tone="secondary">{t('foodReview.decide.asSession', { id: reviewerId })}</Text>
            {pending === undefined ? (
              <LayoutRow gap={2} align="stretch" wrap>
                {actions.map((a, i) => (
                  <Button key={a} variant={i === 0 ? 'primary' : a === 'reject' ? 'danger' : 'secondary'} onClick={() => start(a)} disabled={busy}>{v('food.review_action', a)}</Button>
                ))}
              </LayoutRow>
            ) : (
              <Stack gap={3}>
                <Strong as="p" role="body">{v('food.review_action', pending)}</Strong>
                {pending === 'appeal' && (
                  <Field label={t('foodReview.field.outcome')} htmlFor="fr-outcome">
                    <Select id="fr-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value as 'approved' | 'rejected')}>
                      <option value="approved">{t('foodReview.outcome.approved')}</option>
                      <option value="rejected">{t('foodReview.outcome.rejected')}</option>
                    </Select>
                  </Field>
                )}
                <Field label={t('foodReview.field.reasonCode')} htmlFor="fr-reason" hint={t('foodReview.field.reasonCode.hint')}>
                  <TextInput id="fr-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} placeholder={t('foodReview.field.reasonCode.ph')} />
                </Field>
                <Field label={t('foodReview.field.note')} htmlFor="fr-note">
                  <TextArea id="fr-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('foodReview.field.note.ph')} />
                </Field>
                <LayoutRow gap={2} align="stretch">
                  <Button variant={CONFIRM_VARIANT[pending]} onClick={confirm} disabled={busy || reasonCode.trim().length === 0}>{t('foodReview.decide.confirm')}</Button>
                  <Button variant="ghost" onClick={reset} disabled={busy}>{t('ui.cancel')}</Button>
                </LayoutRow>
              </Stack>
            )}
          </>
        )}
      </section>
    </Stack>
  );
}
